// Source.tsx
import React, { useContext, useEffect, useCallback, useState, useRef, useMemo } from 'react';
import styled from 'styled-components';
import type { Source, UUID, Note, Annote, CardSize } from '@annote/types';
import { ConnectDragSource } from 'react-dnd';
import { text, colors, dimensions } from '@annote/core/styles';
import { ShownIdsContext } from 'src/context/ShownContext';
import { getDomain } from '@annote/core/utils/urls';
import NumberOfAnnotes from 'src/components/NumberOfAnnotes';
import { Favicon } from 'src/components/Favicon';
import { annoteIndex } from 'src/context/annoteIndex';
import { useNotes } from 'src/context/noteCacheHooks';
import { useDndRef } from 'src/helpers/useDndRef';
import { AnnoteCard } from '../annote/Annote';
import SourceReader from './Reader';

const Title = styled.span`
  font-family: ${text.family.reading};
  font-weight: ${text.weight.regular};
  font-size: 20px;
  font-style: normal;
  line-height: ${text.height.header};
  color: ${colors.text.header};
  flex: 1;
  padding: 0;
  margin: 0;

  &:empty::before {
    content: attr(placeholder);
  }
`;

const Wrapper = styled.div<{ size: CardSize; inPanel: boolean }>`
  display: flex;
  flex-direction: column;
  // TODO - if we get rid of "Large" size in the surface then we don't need this inPanel var
  width: ${({ size, inPanel }) => {
    if (inPanel) return '100%';
    return size === 'LARGE' ? `${dimensions.oneInchRem * 8.5}rem` : '37.5rem';
  }};
  ${({ inPanel }) => !inPanel && `max-height: ${dimensions.oneInchRem * 11}rem;`}
  position: relative;
  box-shadow: 0px 1px 2px rgba(15, 15, 15, 0.1);
  border-radius: 2px;
  overflow: ${({ size, inPanel }) => {
    if (inPanel) return 'visible';
    return size === 'LARGE' ? 'auto' : 'hidden';
  }};
  ${({ inPanel }) => !inPanel && 'contain: paint;'}
  ${({ inPanel }) => inPanel && `flex: 1; min-height: 0;`}
`;

const SourceURL = styled.a`
  color: ${colors.text.subtitle};
  font-family: ${text.family.spartan};
  font-optical-sizing: auto;
  font-size: ${text.size.small};
  font-style: normal;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const SourceInfoWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  padding: 2.25rem 2rem;
  flex-direction: column;
  justify-content: flex-end;
  gap: 1.25rem;
  flex-shrink: 0;
`;

const Info = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  align-self: stretch;
`;

const SourceURLWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AnnotesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const AnnotesList = styled.ol`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  padding-bottom: 2.25rem;
`;

const AnnoteListItem = styled.li`
  position: relative;
`;

const AnnotesDivider = styled.div`
  width: 100%;
  opacity: 0.5;
  background: linear-gradient(180deg, #adadad 0%, rgba(217, 217, 217, 0.25) 47.92%, rgba(217, 217, 217, 0) 100%);
  height: 0.75rem;
`;

// Simple div to mark the draggable area
const DraggableArea = styled.div`
  cursor: pointer;
`;

const HeaderContainer = styled.div`
  flex-shrink: 0;
`;

const FullHeader = styled.div<{ isSticky?: boolean }>`
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 2.25rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  background: white;
  opacity: ${({ isSticky }) => (isSticky ? 0.95 : 1)};
  backdrop-filter: ${({ isSticky }) => (isSticky ? 'blur(32px)' : 'none')};
  transition: opacity 0.2s ease-in-out, backdrop-filter 0.2s ease-in-out;
`;

const FullDataLine = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${dimensions.oneInchRem / 4}rem ${dimensions.oneInchRem}rem;
`;

const FullHeaderSpacer = styled.div`
  height: ${dimensions.oneInchRem / 4}rem;
`;

const FullInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${dimensions.oneInchRem / 4}rem;
`;

const FullAuthor = styled.span`
  color: ${colors.text.primary};
  text-align: center;
  font-family: ${text.family.spartan};
  font-size: ${text.size.primary};
  font-style: normal;
  font-weight: ${text.weight.thin};
  line-height: normal;
`;

const NumberOfAnnotesWrapper = styled.div`
  /* This wrapper will be hidden by parent CardOutline when isSuggestion is true */
`;

const FullTitle = styled.span<{ isSticky?: boolean }>`
  color: ${colors.text.header};
  text-align: center;
  font-family: ${text.family.reading};
  font-size: ${({ isSticky }) => (isSticky ? '1.5rem' : text.size.sourceTitle)};
  font-style: italic;
  font-weight: ${text.weight.regular};
  line-height: 120%;
  transition: font-size 0.2s ease-in-out;
`;

interface SourceProps {
  source: Source;
  dragRef?: ConnectDragSource; // Make dragRef optional
  inPreview?: boolean;
  readOnly?: boolean;
  forceSize?: CardSize; // Optional override for display size
  inPanel?: boolean; // When true, removes max-height to let panel handle scrolling
}

export const SourceCard: React.FC<SourceProps> = ({
  source,
  dragRef,
  inPreview = false,
  readOnly = false,
  forceSize,
  inPanel = false,
}) => {
  const { shownIds, showAafterB, hide, shownSizes, updateSize } = useContext(ShownIdsContext);
  const [isTitleSticky, setIsTitleSticky] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Get shown annotes efficiently using annoteIndex
  const annoteIds = useMemo(() => annoteIndex.getAnnoteIds(source.links), [source.links]);
  const shownAnnoteIds = useMemo(
    () => annoteIds.filter((linkId: UUID) => shownIds.includes(linkId)),
    [annoteIds, shownIds],
  );
  const shownAnnotes = useNotes(shownAnnoteIds).filter((n): n is Note => !!n);

  const size = forceSize || (readOnly ? 'SMALL' : shownSizes[source.id] || 'SMALL');
  const cleanURL = getDomain(source.url);

  // If you start out with an annote, switch to ANNOTES mode
  useEffect(() => {
    if (inPreview) return;
    if (size !== 'SMALL') return;
    if (shownAnnoteIds.length === 0) return;
    updateSize(source.id, 'MEDIUM');
  }, [shownAnnoteIds.length > 0 && size, inPreview, source.id, updateSize]);

  // Handle scroll to detect when title becomes sticky
  useEffect(() => {
    if (size !== 'LARGE' || !wrapperRef.current) return;

    const handleScroll = (): void => {
      const wrapper = wrapperRef.current;
      if (!wrapper) {
        return;
      }

      const { scrollTop } = wrapper;
      const headerSpacer = wrapper.querySelector('[data-header-spacer]') as HTMLElement;

      if (headerSpacer) {
        const spacerHeight = headerSpacer.offsetHeight;
        setIsTitleSticky(scrollTop > spacerHeight);
      }
    };

    const wrapper = wrapperRef.current;
    wrapper.addEventListener('scroll', handleScroll);

    // eslint-disable-next-line consistent-return
    return () => {
      wrapper.removeEventListener('scroll', handleScroll);
    };
  }, [size]);

  // When display mode changes, we need to show or hide the annotes
  const changeDisplayMode = useCallback(
    (mode: CardSize): void => {
      if (mode === 'MEDIUM' || mode === 'LARGE') {
        showAafterB(annoteIds, source.id);
      } else {
        // minimal mode
        hide(annoteIds, true);
      }
      updateSize(source.id, mode);
    },
    [annoteIds, source.id, showAafterB, hide, updateSize],
  );

  const SourceUrlJsx = (
    <SourceURLWrapper>
      <Favicon url={source.url} />
      <SourceURL href={source.url} target="_blank" rel="noopener noreferrer">
        {cleanURL}
      </SourceURL>
    </SourceURLWrapper>
  );

  const NumOfAnnotesJsx = (
    <NumberOfAnnotesWrapper
      data-number-of-annotes
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        changeDisplayMode(size === 'MEDIUM' ? 'SMALL' : 'MEDIUM');
      }}
    >
      <NumberOfAnnotes annoteCount={source.links.length} />
    </NumberOfAnnotesWrapper>
  );

  const setRef = dragRef ? useDndRef(dragRef) : undefined;

  return (
    <Wrapper size={size} inPanel={inPanel} className="nowheel" ref={wrapperRef}>
      {size !== 'LARGE' && (
        <>
          <HeaderContainer>
            <DraggableArea ref={setRef}>
              <SourceInfoWrapper>
                <Title>{source.title}</Title>
                <Info>
                  {SourceUrlJsx}
                  {NumOfAnnotesJsx}
                </Info>
              </SourceInfoWrapper>
            </DraggableArea>
            {size === 'MEDIUM' && shownAnnotes.length > 0 && <AnnotesDivider />}
          </HeaderContainer>
          {size === 'MEDIUM' && shownAnnotes.length > 0 && (
            <AnnotesContainer className="nodrag">
              <AnnotesList>
                {shownAnnotes.map((annote) => (
                  <AnnoteListItem key={annote.id}>
                    <AnnoteCard key={annote.id} annote={annote as Annote} />
                    <AnnotesDivider />
                  </AnnoteListItem>
                ))}
              </AnnotesList>
            </AnnotesContainer>
          )}
        </>
      )}
      {size === 'LARGE' && (
        <>
          <FullHeaderSpacer data-header-spacer />
          <FullHeader isSticky={isTitleSticky}>
            <FullTitle isSticky={isTitleSticky}>{source.title}</FullTitle>
          </FullHeader>
          <DraggableArea ref={setRef}>
            <FullInfo>{source.details.author && <FullAuthor>{source.details.author}</FullAuthor>}</FullInfo>
            <FullDataLine>
              {SourceUrlJsx}
              {NumOfAnnotesJsx}
            </FullDataLine>
          </DraggableArea>
          <SourceReader url={source.url} id={source.id} value={source.value} />
        </>
      )}
    </Wrapper>
  );
};

export default React.memo(SourceCard);
